import { useState, useEffect } from "react";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import "../ViewEditPlan/ViewEditPlan.scss";
import BackArrow from "../../../assets/images/vactor/back-arrow.svg";
import InfoWarning from "../../../assets/images/vactor/info-warning.svg";
import {
  Autocomplete,
  ListItem,
  ListItemText,
  Stack,
  styled,
  Switch,
  TextField,
} from "@mui/material";
import {
  LightButton,
  PrimaryButton,
} from "../../AllButtons/AllButtons";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Store/store";
import { createPlans, viewSpecificPlan } from "../../../Services/admin.service";
import { openSnackbar } from "../../../Slice/snackbarSlice";
import Loader from "../../Loader/Loader/Loader";

const selectDuration = [
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];
const selectToken = [{ label: "Unlimited" }, { label: "Limited" }];

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#4F46E5",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

const ViewEditPlan = () => {

  const [amount, setAmount] = useState<number | string>(0);
  const [tokens, setTokens] = useState<any>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const specificPlan = useAppSelector(state => state.admin.specificPlan);
  const isViewMode = localStorage.getItem('specificPlan')==='true'?true:false;

  const [tokenLimit, setTokenLimit] = useState<any>(null);
  const [features, setFeatures] = useState([
    { text: "Unlimited access to all courses", enabled: true },
    { text: "Personal learning assistant", enabled: true },
    { text: "Progress tracking & certificates", enabled: true },
  ]);

  useEffect(() => {
    const fetchSpecificPlan = async () => {
      await dispatch(viewSpecificPlan(localStorage.getItem("planId"))); 
    }
    if(localStorage.getItem("planId")){
      fetchSpecificPlan();
    }
  }, []);
  
  useEffect(() => {
    if (isViewMode && specificPlan?.data) {
      const planData = specificPlan.data;

      setAmount(planData.amount || 0);
      setTokens(planData.tokens === -1 ? "∞" : planData.tokens || 0);
      setTokenLimit(planData.tokens === -1 ? { label: "Unlimited" } : { label: "Limited" });

      // setFeatures(
      //   planData.features?.map((f: any) => ({
      //     text: f.feature_name,
      //     enabled: f.enabled,
      //   })) || []
      // );
    }
  }, [specificPlan, isViewMode]);

  const initialPlanValues = isViewMode && specificPlan?.data
  ? {
      planName: specificPlan.data.name || '',
      tagline: specificPlan.data.plan_description || '',
      amount: specificPlan.data.amount || 0,
      duration: selectDuration.find(
        (d) => d.value === specificPlan.data.plan_duration_type
      ) || null,
      tokenLimit:
        specificPlan.data.tokens === -1
          ? { label: "Unlimited" }
          : { label: "Limited" },
      tokens: specificPlan.data.tokens === -1 ? "∞" : specificPlan.data.tokens || 0,
      features: specificPlan.data.features?.map((f: any) => ({
        text: f.feature_name,
        enabled: f.enabled,
      })) || [],
    }
  : {
      planName: '',
      tagline: '',
      amount: 0,
      duration: null,
      tokenLimit: null,
      tokens: 0,
      features: [
        { text: 'Unlimited access to all courses', enabled: true },
        { text: 'Personal learning assistant', enabled: true },
        { text: 'Progress tracking & certificates', enabled: true },
      ],
    };

  const PlanFormSchema = Yup.object().shape({
  planName: Yup.string().transform((value) => (value ? value.trim() : value))
    .test(
      "no-only-whitespace",
      "Plan Name cannot be empty or only whitespace",
      (value) => !!value && value.trim().length > 0
    )
    .required('Plan Name is required'),
    tagline: Yup.string().transform((value) => (value ? value.trim() : value)).test(
      "no-only-whitespace",
      "Tagline cannot be empty or only whitespace",
      (value) => !!value && value.trim().length > 0
    )
    .max(30, 'Tagline must be at most 30 characters')
    .required('Tagline is required'),
  duration: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required('Duration is required'),
  tokenLimit: Yup.object()
    .shape({
      label: Yup.string().required(),
    })
    .nullable()
    .required('Token Limit is required'),

  amount: Yup.number()
    .typeError('Amount must be a number')
    .moreThan(0, 'Amount must be greater than 0'),
  tokens: Yup.mixed().test(
    'tokens-validation',
    'Tokens must be greater than 0',
    function (value) {
      const { tokenLimit } = this.parent;
      if (tokenLimit?.label === 'Unlimited') {
        return true;
      }
      return typeof value === 'number' && value > 0;
    }
  ),
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues: initialPlanValues,
    enableReinitialize: true,
    validationSchema: PlanFormSchema,
    onSubmit: (values:any) => {   
      handleCreate(values);
    },
  });

  const handleAmountIncrement = () => {
    if (!isViewMode) {
      setAmount((prev: any) => {
        const updated = Number(prev || 0) + 1;
        setFieldValue("amount", updated, true);
        return updated;
      });
    }
  };

  const handleAmountDecrement = () => {
    if (!isViewMode) {
      setAmount((prev: any) => {
        const updated = Math.max(Number(prev || 0) - 1, 0);
        setFieldValue("amount", updated, true); 
        return updated;
      });
    }
  };

  const handleTokenIncrement = () => {
    if (!isViewMode) {
      setTokens((prev: any) => {
        const updated = Number(prev || 0) + 1;
        setFieldValue("tokens", updated, true); 
        return updated;
      });
    }
  };

  const handleTokenDecrement = () => {
    if (!isViewMode) {
      setTokens((prev: any) => {
        const updated = Math.max(Number(prev || 0) - 1, 0);
        setFieldValue("tokens", updated, true); 
        return updated;
      });
    }
  };

  const handleAmountChange = (e: any) => {
    if (!isViewMode) {
      const value = e.target.value;
      if (value === "" || /^\d*$/.test(value)) {
        const numericValue = value === "" ? "" : Number(value);
        setFieldValue("amount", numericValue, true);  
        setAmount(numericValue);
      }
    }
  };

  const handleTokenChange = (e: any) => {
    if (!isViewMode) {
      const value = e.target.value;
      if (value === "" || /^\d*$/.test(value)) {
        const numericValue = value === "" ? "" : Number(value);
        setFieldValue("tokens", numericValue, true); 
        setTokens(numericValue);
      }
    }
  };

  const handleFeatureToggle = (index: number) => {
    if (!isViewMode) {
      setFeatures((prev) =>
        prev.map((feature, i) =>
          i === index ? { ...feature, enabled: !feature.enabled } : feature
        )
      );
      setFieldValue("features", features, true); 
    }
  };

  const handleFeatureTextChange = (index: number, text: string) => {
    if (!isViewMode) {
      setFeatures((prev) =>
        prev.map((feature, i) => (i === index ? { ...feature, text } : feature))
      );
      setFieldValue("features", features, true); 
    }
  };

  const prepareRequestBody = (values:any) => {
    const enabledFeatures = features
      // .filter((feature) => feature.enabled && feature.text.trim())
      .map((feature) => ({
        feature_name: feature.text,
        feature_description: feature.text,
        enabled: feature.enabled,
      }));

    const requestBody = {
      plan_name: values.planName,
      plan_price: Number(amount) || 0,
      tokens: values.tokenLimit?.label === "Unlimited" ? -1 : Number(tokens) || 0,
      plan_description: values.tagline,
      plan_duration_type: values.duration?.value || "monthly",
      created_by: localStorage.getItem("id")||"",
      features: enabledFeatures,
    };

    return requestBody;
  };

  const handleCreate = async(values:any) => {
    setIsLoading(true);
    if (!isViewMode) {
      const requestBody = prepareRequestBody(values);
      await dispatch(createPlans(requestBody));
      dispatch(openSnackbar({message:"Plan Created",severity:"success"}));
      setIsLoading(false);
      navigate("/admin/plan_management");    
    }

  };

  return (
    <>
      <div className="admin-content-wrapper">
        <div className="admin-content-subheader">
          <div className="admin-content-subheader-title">
            <Link to="/admin/plan_management">
              <img src={BackArrow} alt="" />
            </Link>
            {isViewMode ? "View Plan" : "Create Plan"}
          </div>
        </div>
        <div className="view-edit-plan-wrapper">
          {isLoading && (<Loader/>)}
          <div className="view-edit-form">
            <form onSubmit={handleSubmit}>
              <div className="row gy-4">
                <div className="col-md-6">
                <div
                  className="input-form-field-wrapper"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <label htmlFor="planName">
                    Plan Name <span className="error">*</span>
                  </label>
                  <TextField
                    className="out-label-input-text"
                    id="planName"
                    name="planName"
                    placeholder="Enter Plan Name"
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    value={values.planName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isViewMode}
                    error={touched.planName && Boolean(errors.planName)}
                    helperText={
                        touched.planName && typeof errors.planName === "string" ? errors.planName : ""
                    }
                  />                  
                </div>
              </div>
              <div className="col-md-6">
                  <div
                    className="input-form-field-wrapper"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label htmlFor="tagline">Tagline <span className="error">*</span></label>
                    <TextField
                      className="out-label-input-text"
                      id="tagline"
                      name="tagline"
                      placeholder="Best for dedicated learners"
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      value={values.tagline}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isViewMode}
                      error={touched.tagline && Boolean(errors.tagline)}
                      helperText={
                            touched.tagline && typeof errors.tagline === "string"
                              ? errors.tagline
                              : ""
                      }
                    />                   
                    <div className="input-text-warning">
                      <img src={InfoWarning} alt="" /> Max 30 letters only
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div
                    className="input-form-field-wrapper"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label htmlFor="">Enter an amount (USD $)</label>
                    <div className={`plus-minus-counter ${isViewMode ? "disabled" : ""}`}>
                      <button 
                        type="button" 
                        onClick={handleAmountDecrement}
                        disabled={isViewMode}
                      >
                        −
                      </button>
                      <TextField
                          type="number"
                          name="amount"
                          className="input-counter"
                          placeholder="Enter amount"
                          value={amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numericValue = value === "" ? "" : Number(value);
                            handleAmountChange(e);                          
                            setFieldValue("amount", numericValue, true);
                          }}
                          onBlur={handleBlur}
                          disabled={isViewMode}
                          error={touched.amount && Boolean(errors.amount)}
                          
                        />                    
                      <button 
                        type="button" 
                        onClick={handleAmountIncrement}
                        disabled={isViewMode}
                      >
                        +
                      </button>
                    </div>
                    {touched.amount && typeof errors.amount === "string" && (
                        <p
                          className="form-error"                         
                        >
                          {typeof errors.amount === "string" && errors.amount}
                        </p>
                      )} 
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="input-form-field-wrapper">
                    <label htmlFor="">Duration <span className="error">*</span></label>
                    <Autocomplete
                      id="duration"
                      className="grade"
                      options={selectDuration}
                      getOptionLabel={(option) => option.label}
                      value={values.duration}
                      onChange={(_, newValue) => {
                        setFieldValue("duration", newValue);
                      }}
                      onBlur={() => {
                        setFieldTouched("duration", true, true);
                      }}
                      disabled={isViewMode}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="out-label-input-text"
                          placeholder="Select Duration"
                          margin="normal"
                          variant="outlined"
                          disabled={isViewMode}
                          error={Boolean(touched.duration && errors.duration)}
                          helperText={
                            touched.duration && typeof errors.duration === "string"
                              ? errors.duration
                              : ""
                          }
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem {...props} className="autocomplete-list-items">
                          <ListItemText primary={option.label} />
                        </ListItem>
                      )}
                    />                 
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="input-form-field-wrapper">
                    <label htmlFor="">Token Limit</label>
                    <Autocomplete
                      id="tokenLimit"
                      className="grade"
                      options={selectToken}
                      getOptionLabel={(option) => option.label}
                      value={values.tokenLimit}
                      onChange={(event, newValue) =>{setTokenLimit(newValue); setFieldValue("tokenLimit", newValue);}}
                      onBlur={() => setFieldTouched('tokenLimit', true, true)}
                      disabled={isViewMode}
                      isOptionEqualToValue={(option, value) => option.label === value?.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="out-label-input-text"
                          placeholder="Select Tokens"
                          margin="normal"
                          variant="outlined"
                          disabled={isViewMode}
                          error={touched.tokenLimit && Boolean(errors.tokenLimit)}
                          helperText={
                            touched.tokenLimit && typeof errors.tokenLimit === 'string'
                              ? errors.tokenLimit
                              : ''
                          }
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem {...props} className="autocomplete-list-items">
                          <ListItemText primary={option.label} />
                        </ListItem>
                      )}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div
                    className="input-form-field-wrapper"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label htmlFor="">Number of Tokens</label>
                    <div
                      className={`plus-minus-counter ${
                        tokenLimit?.label === "Unlimited" || isViewMode ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={handleTokenDecrement}
                        disabled={tokenLimit?.label === "Unlimited" || isViewMode}
                      >
                        −
                      </button>
                      <TextField
                        type="number"
                        name="tokens"
                        className="input-counter"
                        placeholder="Enter number of tokens"
                        value={tokenLimit?.label === "Unlimited" ? "∞" : tokens}
                        onChange={(e) => {
                            const numericValue = e.target.value === "" ? "" : Number(e.target.value);
                            setFieldValue("tokens", numericValue, true);
                            handleTokenChange(e);
                        }}
                        onBlur={handleBlur}
                        disabled={tokenLimit?.label === "Unlimited" || isViewMode}
                        error={touched.tokens && Boolean(errors.tokens)}                 
                      />                    
                      <button
                        type="button"
                        onClick={handleTokenIncrement}
                        disabled={tokenLimit?.label === "Unlimited" || isViewMode}
                      >
                        +
                      </button>
                    </div>   
                      {touched.tokens && typeof errors.tokens === "string" && (
                        <p
                          className="form-error"
                        >
                          {typeof errors.tokens === "string" && errors.tokens}
                        </p>
                      )}                 
                  </div>
                </div>

                <div className="col-md-12">
                  <div
                    className="input-form-field-wrapper"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <label htmlFor="">Features</label>

                    {features.map((feature, index) => (
                      <div className="feature-switch-filed" key={index}>
                        <TextField
                          className="out-label-input-text"
                          id={`feature-${index}`}
                          name={`feature-${index}`}
                          placeholder="Enter feature description"
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          value={feature.text}
                          onChange={(e) =>
                            handleFeatureTextChange(index, e.target.value)
                          }
                          disabled={isViewMode}
                        />
                        <div className="mb-2">
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <AntSwitch
                              checked={feature.enabled}
                              onChange={() => handleFeatureToggle(index)}
                              inputProps={{ "aria-label": "ant design" }}
                              disabled={isViewMode}
                            />
                          </Stack>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Only show the duplicate features section and add feature in create mode */}
                {/* {!isViewMode && (
                  <div className="col-md-12">
                    <div
                      className="input-form-field-wrapper"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <label htmlFor="">Features</label>

                      {features.map((feature, index) => (
                        <div className="feature-switch-filed" key={index}>
                          <TextField
                            className="out-label-input-text"
                            id={`feature-${index}`}
                            name={`feature-${index}`}
                            placeholder="Enter feature description"
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            value={feature.text}
                            onChange={(e) =>
                              handleFeatureTextChange(index, e.target.value)
                            }
                          />
                          <div className="mb-2 feature-button-group">
                            <Button className="close-fe-btn">
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <path d="M4.99956 3.93949L8.71206 0.22699L9.77256 1.28749L6.06006 4.99999L9.77256 8.71249L8.71206 9.77299L4.99956 6.06049L1.28706 9.77299L0.226562 8.71249L3.93906 4.99999L0.226562 1.28749L1.28706 0.22699L4.99956 3.93949Z" fill="#DF1C41"/>
                              </svg>
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="feature-switch-filed">
                        <TextField
                          className="out-label-input-text"
                          placeholder="Enter feature description"
                          margin="normal"
                          variant="outlined"
                          fullWidth
                        />
                        <div className="mb-2 feature-button-group">
                          <Button className="add-fe-btn">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M5.25 5.25V0.75H6.75V5.25H11.25V6.75H6.75V11.25H5.25V6.75H0.75V5.25H5.25Z" fill="#4F46E5"/>
                            </svg>
                          </Button>
                        </div>
                      </div>
                      <div className="notice-guide-list">
                        <List>
                          <ListItem>
                            Min one and max Five words allowed.
                          </ListItem>
                          <ListItem>
                            Max 5 feature will allowed.
                          </ListItem>
                        </List>
                      </div>
                    </div>
                  </div>
                )} */}

                <div className="col-md-12">
                  <div className="view-edit-action-row">
                    {!isViewMode && (
                      <PrimaryButton type="submit">
                        Create
                      </PrimaryButton>
                    )}
                    <LightButton
                      onClick={() => {
                        navigate("/admin/plan_management");
                      }}
                    >
                      {isViewMode ? "Back" : "Cancel"}
                    </LightButton>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewEditPlan;